// import STORAGE_MAP from '@/utils/storageMap'
import { history, getLocale } from 'umi'
import moment from 'moment'
import { unstable_batchedUpdates } from 'react-dom'

export {
  redirectToApp,
  pushToApp,
  redirectMessaging,
  redirectCommerce,
  pushToCommerce,
  redirectToCrm,
} from './redirect'

export const TimeFormat = 'YYYY/MM/DD' as const
export const TimeFormatMinutes = `${TimeFormat} HH:mm` as const
export const TimeFormatEXACT = `${TimeFormat} HH:mm:ss` as const

type IFilterBetweenCondition<K extends string = string> = {
  condition: 'between'
  key?: K
  from?: number
  to?: number
  value?: undefined
}

type IFilterInCondition<K extends string = string> = {
  condition: 'in' | 'nin'
  key?: K
  /** @summary 注意value是一个用逗号链接的字符串数组, eg：1,2,3,4 \ string */
  value: string
}

type IFilterSimpleCondition<K extends string = string> = {
  condition: '=' | '<>' | 'like'
  key?: K
  value?: string | number | boolean
}

export type IFilterCondition<K extends string = string> =
  | IFilterBetweenCondition<K>
  | IFilterInCondition<K>
  | IFilterSimpleCondition<K>

type TimeFormatType = typeof TimeFormat | typeof TimeFormatMinutes | typeof TimeFormatEXACT

const getApiHost = () => window.__app?.API_HOST
const getIDaasPath = () => window?.__app?.IDAAS_PATHNAME

export const toLogin = () => {
  let locale: string = getLocale() || 'zh'
  if (locale.startsWith('zh')) locale = 'zh'
  else if (locale.startsWith('en')) locale = 'en'
  window.name = JSON.stringify({ lang: locale })

  window.location.href = [getApiHost(), getIDaasPath()].join('')
}

export const iteratorSerial = (...args: (() => Promise<any>)[]) => (
  isExec: boolean | ((data: any, index: number) => boolean) = true,
) =>
  new Promise<void>((resolve, reject) => {
    let index = 0
    const iter = () => {
      if (!args.length) resolve()
      index += 1
      const handler = args.shift()
      handler?.()
        .then((data) => {
          if (typeof isExec === 'function') {
            const condition = isExec as (data: any, index: number) => boolean
            const shouldNext = condition(data, index)
            if (shouldNext) iter()
          }
          if (typeof isExec === 'boolean') {
            if (!isExec) return
            iter()
          }
        })
        .catch((err) => {
          reject(err)
        })
    }
    iter()
  })

export const downLoadFile = (path: string) => {
  const url = window.__app.appendApiDomain(path)
  let linkEl: HTMLAnchorElement | null = document.createElement('a')
  linkEl.href = url
  linkEl.target = '_blank'
  linkEl.rel = 'noreferrer'
  linkEl.click()
  Promise.resolve().then(() => {
    linkEl = null
  })
}

export const downloadByLink = (path: string) => {
  let opener = window.open(path)
  opener = null
}

export const getPathDetail = (url: string) => {
  let a: HTMLAnchorElement | null = document.createElement('a')
  a.href = url
  const { host, port: urlPort, search, pathname, hostname } = a
  const details = {
    host,
    hostname,
    port: urlPort,
    search,
    pathname,
    path: `${pathname}${search}`,
  }
  a = null
  return details
}

export const getBackPageUrl = (fallback: string) => {
  const url = document.referrer
  const { path, pathname } = getPathDetail(url)
  if (path && pathname !== history.location.pathname) {
    return path
  }
  return fallback
}

export function isThenable<T = any>(r: any): r is Promise<T> {
  return typeof r === 'object' && typeof r?.then === 'function' && typeof r?.catch === 'function'
}

export const dateShowFormat = (
  date?: number | string,
  format: TimeFormatType = TimeFormatMinutes,
) => {
  if (!date) return '--'
  const momentDate = moment(Number(date))
  if (!momentDate.isValid()) {
    return '--'
  }
  return momentDate.format(format)
}

export const toMoment = (date?: number | string) => {
  if (!date) return undefined
  const momentDate = moment(date)
  if (!momentDate.isValid()) {
    return undefined
  }
  return momentDate
}

dateShowFormat.format = {
  TimeFormat,
  TimeFormatMinutes,
  TimeFormatEXACT,
}

type IConditionArgs = (() => boolean) | boolean
type INone = null | undefined | void
class C<T extends any> {
  result = true

  private output: T | undefined

  public constructor(initResult?: boolean, output?: T) {
    this.result = Boolean(initResult)
    this.output = output
  }

  private getArgsResult = (condition: IConditionArgs) => {
    if (typeof condition === 'function') return condition()
    return condition
  }

  public and(condition: IConditionArgs) {
    this.result &&= this.getArgsResult(condition)
    return this
  }

  public or(condition: IConditionArgs) {
    this.result ||= this.getArgsResult(condition)
    return this
  }

  public right(value: T | ((output: T | INone) => T | INone)) {
    if (!this.result) return this
    if (typeof value === 'function') {
      const valFn = value as (output: T | INone) => T
      this.output = valFn(this.output)
      return this
    }
    this.output = value
    return this
  }

  public left(value?: T | ((output: T | INone) => T | INone)) {
    if (this.result) return this
    if (typeof value === 'function') {
      const valFn = value as (output: T | INone) => T
      this.output = valFn(this.output)
      return this
    }
    this.output = value
    return this
  }

  public reset(condition: IConditionArgs) {
    this.result = this.getArgsResult(condition)
    return this
  }

  public both(fn: () => unknown) {
    fn()
    return this
  }

  public todo(executor: () => unknown) {
    if (this.result) executor()
    return this
  }

  public ordo(executor: () => unknown) {
    if (!this.result) executor()
    return this
  }

  public end() {
    return this.output as T
  }
}

export function either<T extends any = null>(
  condition: boolean | (() => boolean),
  initialOutput?: T,
) {
  let result = condition
  if (typeof condition === 'function') {
    result = condition()
  }
  return new C<T>(result as boolean, initialOutput)
}

/** 整数位数以后再处理... */
export const correctedNumberValue = (value: string, decimal: number = 2, integer?: number) => {
  let correctedValue = value

  if (decimal === 0) {
    correctedValue = value
      .replace(/[^\d]/g, '')
      .replace(/^\./g, '')
      .replace('.', '')
      .replace(/\./g, '')
      .replace(/^(-)*(\d+)$/, '$1$2')
    return correctedValue
  }

  const decimalCountRex = new RegExp(`\\.{${decimal},}`, 'g')

  const decimalFormatRex = new RegExp(`^(-)*(\\d+)\\.(${Array(decimal).fill('\\d').join('')}).*$`)

  correctedValue = value
    .replace(/[^\d.]/g, '') // 清除"数字"和"."以外的字符
    .replace(/^\./g, '') // 验证第一个字符是数字而不是.
    .replace(decimalCountRex, '.')
    .replace('.', '$#$')
    .replace(/\./g, '')
    .replace('$#$', '.') // 只允许输入一个小数点
    .replace(decimalFormatRex, '$1$2.$3') // 只能输入两个小数

  return correctedValue
}

function isUnUseValue(value: any) {
  return value === undefined || value === null || value === '' || Number.isNaN(value)
}

export const getFilters = <T extends Record<string, any> = Record<string, any>>(
  result?: T,
  schema?: Partial<
    Record<
      keyof T,
      | IFilterCondition
      | ((raw: T[keyof T], result: T) => IFilterCondition | undefined)
      | IFilterCondition['condition']
    >
  >,
) => {
  const filters = Object.entries(result || {}).flatMap(([key, value]) => {
    const fieldSchema = schema?.[key]
    if (!fieldSchema) return []
    if (typeof fieldSchema === 'string') {
      if (isUnUseValue(value)) return []
      return {
        key,
        value,
        condition: fieldSchema,
      }
    }
    if (typeof fieldSchema === 'object') {
      const res = { key, value, ...fieldSchema }
      if (isUnUseValue(res.value)) return []
      return res
    }
    if (typeof fieldSchema === 'function') {
      const fieldSchemaFn = fieldSchema as (raw: T[keyof T], result: T) => IFilterCondition
      const field = fieldSchemaFn(value, result!)
      if (!field) return []
      const res = { key, value, ...field }

      if (isUnUseValue(res.value)) return []
      return res
    }
    return []
  })
  return filters
}

type TypeEnum =
  | 'Object'
  | 'Array'
  | 'Boolean'
  | 'Symbol'
  | 'Number'
  | 'String'
  | 'Undefined'
  | 'Null'
  | 'Function'
  | 'Date'
  | 'RegExp'

export const isObjectTypeWas = (o: any, type: TypeEnum) => {
  return Object.prototype.toString.call(o) === `[object ${type}]`
}
// 小型调度器，用于处理大量任务，避免阻塞主线程，导致页面卡顿
// 只有当当前任务处理完毕仍有空闲(当前帧剩余时间小于17ms, 小于该值表示当前帧剩余时间不足，大于该值表示当前帧已超时，正在卡顿)时,才会执行回调函数
export const microTask = (task: () => void) => {
  const taskFn = (cb: Function) => {
    const start = performance.now()
    requestAnimationFrame(() => {
      const cur = performance.now()
      const timeGap = cur - start
      if (timeGap < 17) {
        task()
        cb()
        return
      }
      taskFn(cb)
    })
  }

  return new Promise((resolve) => {
    taskFn(resolve)
  })
}

export const sliceSetListState = <T = any>(
  data: T[],
  setter: React.Dispatch<React.SetStateAction<T[]>>,
) => {
  const SLICE_SIZE = 50
  const len = data.length
  if (len < SLICE_SIZE) {
    setter(data)
    return
  }

  const sliceTask = (index: number = 0) => {
    const endIndex = Math.min(index + SLICE_SIZE, len)
    const sliceList = data.slice(index, endIndex)
    if (index >= len || sliceList.length === 0) return

    microTask(() => {
      setter((source) => {
        const ret = source || []
        return ret.concat(sliceList)
      })
    }).then(() => {
      if (endIndex >= len) return
      sliceTask(endIndex)
    })

    // if (endIndex >= len) return
    // sliceTask(endIndex)
  }
  unstable_batchedUpdates(() => {
    sliceTask()
  })
}
